/**
 * HarmonyEmpty — Project Harmony · The Empty State Principle
 *
 * Every empty page should teach, not accuse. The primitive enforces the
 * shape (headline · body · primary action · optional secondary) so no
 * page ever ships a "No campaigns found." dead end.
 */
import { Link } from "react-router-dom";
import type { ReactNode } from "react";

import { Button } from "@/shared/ui/button";

interface HarmonyEmptyProps {
  eyebrow?: string;
  title: string;
  body: string;
  icon?: ReactNode;
  primary?: { label: string; to?: string; onClick?: () => void; testId?: string };
  secondary?: { label: string; to?: string; onClick?: () => void; testId?: string };
  testId?: string;
}

export function HarmonyEmpty({
  eyebrow, title, body, icon, primary, secondary, testId,
}: HarmonyEmptyProps): JSX.Element {
  return (
    <div
      data-testid={testId ?? "harmony-empty"}
      className="mx-auto max-w-xl rounded-2xl border border-dashed border-[color:var(--md-line)] bg-[var(--md-ink-1)]/40 p-8 text-center"
    >
      {icon ? (
        <div className="mx-auto mb-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-[var(--md-amber)]/10 text-[var(--md-amber)]">
          {icon}
        </div>
      ) : null}
      {eyebrow ? (
        <div className="mb-2 text-xs uppercase tracking-widest text-[var(--md-fog)]">
          {eyebrow}
        </div>
      ) : null}
      <h3 className="text-xl font-semibold text-[var(--md-ivory-0)]">{title}</h3>
      <p className="mx-auto mt-3 max-w-md text-sm text-[var(--md-fog)]">{body}</p>
      {(primary || secondary) ? (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          {primary ? (
            primary.to ? (
              <Button asChild data-testid={primary.testId}>
                <Link to={primary.to}>{primary.label}</Link>
              </Button>
            ) : (
              <Button data-testid={primary.testId} onClick={primary.onClick}>
                {primary.label}
              </Button>
            )
          ) : null}
          {secondary ? (
            secondary.to ? (
              <Button asChild variant="ghost" data-testid={secondary.testId}>
                <Link to={secondary.to}>{secondary.label}</Link>
              </Button>
            ) : (
              <Button variant="ghost" data-testid={secondary.testId} onClick={secondary.onClick}>
                {secondary.label}
              </Button>
            )
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
